import { OEP_FILE_CATEGORY, type IOEPSeasonTimeline, type OEPPhase, type OEPRoleKey, type OEPUserSnapshot } from 'agu_oep_core';
import { Up } from '../views/shared/rows';

/** Raw rows the context service loads (one RunViews batch, RLS-scoped server-side) for BuildOEPSnapshot. */
export interface OEPSnapshotRows {
    Roles: ReadonlySet<OEPRoleKey>;
    Me: string;
    Season: { Code: string; Phase: OEPPhase; Timeline: IOEPSeasonTimeline };
    Sections: { ID: string; Name: string }[];
    MyPresentations: { ID: string; Title: string; SectionID: string | null }[];
    MyFiles: { RecordID: string; Category: string; Status: string }[];
    ReleasedReviews: { ID: string; ReviewAssignmentID: string }[];
    MyThankYouNotes: { ReviewID: string }[];
    ThreadMessages: { ConversationID: string; SenderUserID: string; ThreadPosition: number | null }[];
    MyAssignments: { ID: string; PresentationID: string }[];
    AssignedPresentations: { ID: string; SectionID: string | null }[];
    MyReviews: { ReviewAssignmentID: string; Status: string }[];
    MySessions: { ID: string }[];
    SessionPresentations: { ID: string }[];
    MySubmittedNominations: number;
    MyCommittee: { SectionID: string } | null;
    CommitteeNominations: number;
    CommitteeSlateSubmitted: boolean;
}

type ThreadMessage = OEPSnapshotRows['ThreadMessages'][number];

/** A thread is full at three messages (logic.js:563-570: alternating, presenter starts; OEPCore DecideThreadMessage); a full thread awaits nobody. */
const THREAD_MAX = 3;


/**
 * Pure: raw rows → the OEPUserSnapshot nav/tasks/home derive from (agu_oep_core BuildNav/BuildTasks).
 * Every row set is already RLS-scoped to the signed-in user, so this only counts.
 */
export function BuildOEPSnapshot(r: OEPSnapshotRows): OEPUserSnapshot {
    const sectionName = (id: string | null): string => (id ? r.Sections.find((s) => Up(s.ID) === Up(id))?.Name ?? '' : '');

    const threads = new Map<string, ThreadMessage[]>();
    for (const m of r.ThreadMessages) threads.set(m.ConversationID, [...(threads.get(m.ConversationID) ?? []), m]);
    /** Open threads whose last message is (me=true) or is not (me=false) mine. */
    const threadsLastFrom = (me: boolean): number =>
        [...threads.values()].filter((msgs) => {
            const sorted = [...msgs].sort((a, b) => (a.ThreadPosition ?? 0) - (b.ThreadPosition ?? 0));
            const last = sorted[sorted.length - 1];
            return sorted.length < THREAD_MAX && last !== undefined && (Up(last.SenderUserID) === Up(r.Me)) === me;
        }).length;

    const pres = r.MyPresentations[0];
    const hasFile = (category: string): boolean =>
        pres !== undefined && r.MyFiles.some((f) => f.Category === category && f.Status === 'Uploaded' && Up(f.RecordID) === Up(pres.ID));

    const live = new Set(r.MyAssignments.map((a) => Up(a.ID)));
    const submitted = r.MyReviews.filter((x) => live.has(Up(x.ReviewAssignmentID)) && (x.Status === 'Submitted' || x.Status === 'Completed')).length;
    const thanked = new Set(r.MyThankYouNotes.map((n) => Up(n.ReviewID)));

    return {
        Roles: r.Roles,
        Phase: r.Season.Phase,
        Timeline: r.Season.Timeline,
        SeasonCode: r.Season.Code,
        Presenter: r.Roles.has('presenter') && pres !== undefined ? {
            PresentationTitle: pres.Title,
            SectionName: sectionName(pres.SectionID),
            HasDraft: hasFile(OEP_FILE_CATEGORY.Draft),
            HasFinal: hasFile(OEP_FILE_CATEGORY.Final),
            ReleasedReviewCount: r.ReleasedReviews.length,
            UnthankedReviewCount: r.ReleasedReviews.filter((rv) => !thanked.has(Up(rv.ID))).length,
            RepliesFromReviewers: threadsLastFrom(false),
        } : null,
        Reviewer: r.Roles.has('reviewer') ? {
            LiveAssignments: r.MyAssignments.length,
            UnsubmittedReviews: r.MyAssignments.length - submitted,
            SectionNames: [...new Set(r.AssignedPresentations.map((p) => sectionName(p.SectionID)).filter((n) => n.length > 0))],
            ThreadsAwaitingReply: threadsLastFrom(false),
        } : null,
        Nominator: r.Roles.has('nominator') ? {
            SessionCount: r.MySessions.length,
            PresenterCount: r.SessionPresentations.length,
            SubmittedNominations: r.MySubmittedNominations,
        } : null,
        Committee: r.Roles.has('committee') && r.MyCommittee ? {
            SectionName: sectionName(r.MyCommittee.SectionID),
            NominationCount: r.CommitteeNominations,
            SlateSubmitted: r.CommitteeSlateSubmitted,
        } : null,
    };
}
